import AutoFixHighIcon from "@mui/icons-material/DescriptionOutlined";
import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { planner } from "../../../planner-ui";

interface ProcessingStepProps {
    fileName: string
}

const processingSteps = [
    "Uploading document...",
    "Analyzing document structure...",
    "Extracting text and tables...",
    "Identifying job orders...",
    "Matching products and workcenters...",
    "Validating extracted data...",
];

const ProcessingStep: React.FC<ProcessingStepProps> = ({ fileName }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        // Simulate progress through steps
        const stepInterval = setInterval(() => {
            setCurrentStep((prev) => {
                if (prev < processingSteps.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 2000);

        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev < 90) {
                    return prev + Math.random() * 10;
                }
                return prev;
            });
        }, 500);

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, []);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 4,
                }}
            >
                <CircularProgress
                    size={100}
                    thickness={3}
                    sx={{ color: planner.accentSoft }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <AutoFixHighIcon sx={{ fontSize: 40, color: planner.accent }} />
                </Box>
            </Box>

            <Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
                Reading your document
            </Typography>

            <Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
                Processing:{" "}
                <Box component="span" sx={{ fontFamily: planner.fontMono }}>
                    {fileName}
                </Box>
            </Typography>

            <Box sx={{ width: "100%", maxWidth: 400, mt: 3 }}>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(progress, 95)}
                    sx={{
                        height: 4,
                        borderRadius: `${planner.radiusXs}px`,
                        bgcolor: planner.alloy,
                        "& .MuiLinearProgress-bar": {
                            bgcolor: planner.accent,
                            borderRadius: `${planner.radiusXs}px`,
                        },
                    }}
                />
            </Box>

            <Box sx={{ mt: 4, textAlign: "center" }}>
                {processingSteps.map((step, index) => (
                    <Typography
                        key={step}
                        sx={{
                            ...planner.type.body,
                            color: index <= currentStep ? planner.ink : planner.inkMuted,
                            fontWeight: index === currentStep ? 600 : 400,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            mb: 0.5,
                        }}
                    >
                        {index < currentStep ? "✓" : index === currentStep ? "→" : "○"} {step}
                    </Typography>
                ))}
            </Box>

            <Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 4 }}>
                Large files (50+ rows) may take 1-2 minutes to process
            </Typography>
        </Box>
    );
};

export default ProcessingStep;


